import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChatMessage } from '../../entities/ChatMessage';
import { useTeam } from '../../contexts/TeamContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-solid-svg-icons';

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: var(--spacing-4);
  padding: var(--spacing-2);
  background: transparent;
  border-radius: var(--radius-lg);
  border: none;
  min-height: 0;
  display: flex;
  flex-direction: column;
  
  /* Custom scrollbar - pill shape, no arrows */
  &::-webkit-scrollbar {
    width: 0.5rem; /* 8px in rem */
    -webkit-appearance: none;
  }
  
  &::-webkit-scrollbar-button {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--overlay-dark);
    border-radius: var(--spacing-1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-accent-rgb), 0.5);
    border-radius: 999px; /* Pill shape */
    min-height: 2.5rem; /* 40px in rem */
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--color-accent-rgb), 0.7);
  }
  
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--color-accent-rgb), 0.5) var(--overlay-dark);
`;

const Message = styled.div<{ $isUser: boolean }>`
  width: 100%;
  padding: var(--spacing-6) 0;
  background: transparent;
  border-bottom: 1px solid var(--color-border);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: var(--spacing-4) 0;
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: var(--spacing-3) 0;
  }
`;

const MessageInner = styled.div`
  max-width: var(--container-medium);
  margin: 0 auto;
  padding: 0 var(--spacing-8);
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-4);
  
  @media (max-width: 48rem) { /* --breakpoint-tablet */
    padding: 0 var(--spacing-4);
    gap: var(--spacing-3);
  }
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    padding: 0 var(--spacing-3);
    gap: var(--spacing-2);
  }
`;

const MessageAvatar = styled.div<{ $isUser: boolean }>`
  width: 1.875rem; /* 30px in rem */
  height: 1.875rem; /* 30px in rem */
  border-radius: var(--spacing-1);
  background: ${props => props.$isUser 
    ? 'linear-gradient(135deg, var(--color-accent), var(--color-accent-light))' 
    : 'var(--color-success)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: var(--font-size-xs);
  flex-shrink: 0;
  
  @media (max-width: 30rem) { /* --breakpoint-mobile */
    width: 1.625rem; /* 26px in rem */
    height: 1.625rem; /* 26px in rem */
    font-size: var(--font-size-xs);
  }
  
  &::before {
    content: ${props => props.$isUser ? '"VV"' : '"AI"'};
  }
`;

const MessageContent = styled.div<{ $isUser: boolean }>`
  flex: 1;
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: 400;
  line-height: var(--line-height-relaxed);
  letter-spacing: 0.01em;
  word-wrap: break-word;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  p {
    margin: 0 0 var(--spacing-4) 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  code {
    background: var(--overlay-light);
    padding: var(--spacing-1) var(--spacing-2);
    border-radius: var(--spacing-1);
    font-family: var(--font-family-mono);
    font-size: 0.9em;
  }
  
  pre {
    background: var(--color-surface-alt);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--spacing-4);
    overflow-x: auto;
    margin: var(--spacing-4) 0;
    
    code {
      background: none;
      padding: 0;
      border-radius: 0;
    }
  }
  
  ul, ol {
    margin: var(--spacing-4) 0;
    padding-left: var(--spacing-6);
  }
  
  li {
    margin: var(--spacing-2) 0;
  }
  
  blockquote {
    border-left: 0.25rem solid var(--color-accent);
    margin: var(--spacing-4) 0;
    padding-left: var(--spacing-4);
    color: var(--color-text-secondary);
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin: var(--spacing-6) 0 var(--spacing-2) 0;
    font-weight: 600;
    color: var(--color-text-primary);
  }
  
  table {
    border-collapse: collapse;
    width: 100%;
    margin: var(--spacing-4) 0;
  }
  
  th, td {
    border: 1px solid var(--color-border);
    padding: var(--spacing-2);
    text-align: left;
  }
  
  th {
    background: var(--color-surface-alt);
    font-weight: 600;
  }
`;

const WelcomeMessage = styled.div`
  max-width: var(--container-medium);
  margin: var(--spacing-8) auto;
  padding: var(--spacing-8);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 1.1rem;
  font-weight: 400;
  line-height: 1.7;
  letter-spacing: 0.01em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const WelcomeIcon = styled.div`
  font-size: 1.5rem;
  color: var(--color-accent);
  margin-bottom: 0.5rem;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 0.01em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  &::before {
    content: "⏳";
  }
`;

interface ChatMessagesProps {
  messages: ChatMessage[];
  isLoading: boolean;
  customWelcomeMessage?: string;
  showWelcomeMessage?: boolean;
}

export const ChatMessages: React.FC<ChatMessagesProps> = React.memo(({ messages, isLoading, customWelcomeMessage, showWelcomeMessage = true }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { getMatchupTitle, isLoading: teamLoading, getUserTeam, getOpponentTeam } = useTeam();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const hasMessages = messages.length > 0;
  
  const welcomeMessage = React.useMemo(() => {
    if (customWelcomeMessage) return customWelcomeMessage;
    const userTeam = getUserTeam();
    const opponentTeam = getOpponentTeam();
    const userTeamName = userTeam?.teamName || 'your team';
    const opponentTeamName = opponentTeam?.teamName || 'the opposing team';
    return `Hi ${userTeamName}, I'm here to help you prepare for the game against ${opponentTeamName}. Let's start analyzing the data for both teams`;
  }, [customWelcomeMessage, getUserTeam, getOpponentTeam]);

  return (
    <MessagesContainer 
      role="log" 
      aria-label="Chat messages"
      aria-live="polite"
      aria-atomic="false"
    >
      {!hasMessages && showWelcomeMessage && (
        <WelcomeMessage role="status" aria-live="polite">
          <WelcomeIcon aria-hidden="true">
            <FontAwesomeIcon icon={faComments} />
          </WelcomeIcon>
          <div>{welcomeMessage}</div>
        </WelcomeMessage>
      )}
      
      {messages.map((message) => (
        <Message 
          key={message.id} 
          $isUser={message.role === 'user'}
          role="article"
          aria-label={message.role === 'user' ? 'Your message' : 'Assistant message'}
        >
          <MessageInner>
            <MessageAvatar 
              $isUser={message.role === 'user'} 
              aria-label={message.role === 'user' ? 'You' : 'AI Assistant'}
              aria-hidden="true"
            />
            <MessageContent $isUser={message.role === 'user'}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {message.content}
              </ReactMarkdown>
            </MessageContent>
          </MessageInner>
        </Message>
      ))}
      
      {isLoading && (
        <Message 
          $isUser={false}
          role="status"
          aria-live="polite"
          aria-label="Assistant is typing"
        >
          <MessageInner>
            <MessageAvatar $isUser={false} aria-hidden="true" />
            <MessageContent $isUser={false}>
              <TypingIndicator aria-hidden="true">Assistant is typing...</TypingIndicator>
            </MessageContent>
          </MessageInner>
        </Message>
      )}
      
      <div ref={messagesEndRef} aria-hidden="true" />
    </MessagesContainer>
  );
});

ChatMessages.displayName = 'ChatMessages'; 